import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight,
  HeartHandshake, ShieldCheck, BookOpen, PawPrint, Baby,
  Bell, MapPin, Users, Heart, Home, Backpack, ClipboardList,
  FileText, GraduationCap, Award, ShieldAlert, TreePine, Star,
  Globe, Phone, ChevronRight, Zap, Database, BarChart3,
} from "lucide-react";
import { SEO } from "@/components/SEO";

/* ─── DESIGN TOKENS ─── */
const C = {
  navy: "#2D3561",
  orange: "#F5A623",
  olive: "#8B9A2D",
  red: "#E8412A",
  white: "#FFFFFF",
  offwhite: "#f0eee9",
  blue: "#686af3",
  lilac: "#a88efb",
  pink: "#da818d",
  black: "#1a1a1a",
};

const DISPLAY = "'DM Serif Display', 'Ravenna', Georgia, serif";
const BODY = "'Albert Sans', 'Ardena', system-ui, sans-serif";
const MONO = "'IBM Plex Mono', monospace";

/* ─── IMAGES ─── */
const IMG = {
  hero: "https://images.unsplash.com/photo-1600289493088-3054d8a876d0?auto=format&fit=crop&w=1200&q=80",
  problem: "https://images.pexels.com/photos/7182026/pexels-photo-7182026.jpeg?auto=compress&cs=tinysrgb&w=800",
  volunteers: "https://images.pexels.com/photos/7469274/pexels-photo-7469274.jpeg?auto=compress&cs=tinysrgb&w=1200",
  fire: "https://images.unsplash.com/photo-1615092296061-e2ccfeb2f3d6?auto=format&fit=crop&w=1200&q=80",
  portrait: "https://images.unsplash.com/photo-1550953581-a75aa86fec20?auto=format&fit=crop&w=800&q=80",
  cat: "https://images.unsplash.com/photo-1663637875268-7bba6bc372fd?auto=format&fit=crop&w=800&q=80",
};

/* ─── HOVER STYLES (injected via <style> tag) ─── */
const HOVER_CSS = `
  .ea-app-band { transition: filter 0.4s ease, transform 0.4s ease; will-change: transform, filter; }
  .ea-app-band:hover { filter: brightness(1.12); transform: translateY(-3px); }
  .ea-resource-block { transition: transform 0.35s ease, box-shadow 0.35s ease; will-change: transform; }
  .ea-resource-block:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(45,53,97,0.12); }
  .ea-team-card { transition: transform 0.3s ease; will-change: transform; }
  .ea-team-card:hover { transform: translateY(-4px); }
`;

/* ─── PHASE ITEM (hover = slide right like a TOC) ─── */
function PhaseItem({ item, borderTop }) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-center gap-5 md:gap-8 cursor-pointer"
      style={{
        borderTop,
        padding: "24px 0",
        paddingLeft: hovered ? 20 : 0,
        background: hovered ? "rgba(255,255,255,0.08)" : "transparent",
        borderRadius: 4,
        transition: "padding-left 0.3s ease, background 0.3s ease",
      }}
    >
      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: hovered ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.5)", transition: "color 0.3s" }} strokeWidth={2} />
      <p
        className="text-lg md:text-xl font-medium"
        style={{ fontFamily: BODY, color: C.white }}
      >
        {item.text}
      </p>
      <ArrowRight
        className="w-4 h-4 flex-shrink-0 ml-auto"
        style={{ color: C.white, opacity: hovered ? 0.7 : 0, transition: "opacity 0.3s" }}
      />
    </div>
  );
}

/* ─── VIEWPORT ANIMATION HOOK ─── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── COUNT-UP HOOK ─── */
function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const numTarget = parseInt(String(target).replace(/[^0-9]/g, ""), 10) || 0;
          const startTime = performance.now();
          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numTarget));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
          obs.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  const suffix = String(target).replace(/[0-9]/g, "");
  return [ref, count, suffix];
}

function CountUpNumber({ value, label, icon: SIcon, color = C.orange }) {
  const [ref, count, suffix] = useCountUp(value);
  return (
    <div ref={ref} className="py-8" style={{ borderLeft: `3px solid ${color}`, paddingLeft: 24 }}>
      <SIcon className="w-5 h-5 mb-3" style={{ color }} strokeWidth={1.8} />
      <p className="text-4xl md:text-5xl font-bold" style={{ fontFamily: DISPLAY, color }}>
        {count.toLocaleString("es-ES")}{suffix}
      </p>
      <p className="text-xs mt-2" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.5)" }}>{label}</p>
    </div>
  );
}

/* ─── EDITORIAL LABEL ─── */
function EditorialLabel({ number, text, color = C.orange }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      {number && (
        <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color }}>
          {number}
        </span>
      )}
      <span className="block w-12 h-px" style={{ background: color }} />
      <span className="text-[11px] font-semibold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color }}>
        {text}
      </span>
    </div>
  );
}

/* ─── APP BAND (hover = lift + glow) ─── */
function AppBand({ app, idx, navigate }) {
  const [hovered, setHovered] = useState(false);
  const AppIcon = app.icon;
  const bgMap = { conecta: C.blue, prepara: C.orange, aprende: C.lilac, fauna: C.olive, kids: C.pink };
  const bandBg = bgMap[app.id] || C.navy;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer"
      onClick={() => navigate(app.href)}
      data-testid={`hub-app-card-${app.id}`}
      style={{
        background: bandBg,
        filter: hovered ? "brightness(1.15)" : "brightness(1)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "filter 0.4s ease, transform 0.4s ease",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] font-semibold tracking-widest" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.45)" }}>
                0{idx + 1}
              </span>
              <span className="w-10 h-px" style={{ background: "rgba(255,255,255,0.25)" }} />
            </div>
            <div className="flex items-center gap-4 mb-4">
              <AppIcon className="w-7 h-7 flex-shrink-0" style={{ color: C.white }} strokeWidth={1.8} />
              <h3
                className="text-[clamp(2rem,4vw,3.5rem)] tracking-tight leading-[1]"
                style={{ fontFamily: DISPLAY, color: C.white }}
              >
                {app.name}
              </h3>
            </div>
            <p className="text-sm font-semibold tracking-wider uppercase" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)" }}>
              {app.tagline}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
              {app.description}
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 flex flex-col justify-between h-full">
            <div className="space-y-2.5 mb-4">
              {app.features.map((f, fi) => (
                <span key={fi} className="flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                  <f.icon className="w-3.5 h-3.5" style={{ color: "rgba(255,255,255,0.5)" }} strokeWidth={2} />
                  {f.t}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs font-bold tracking-wider uppercase" style={{ fontFamily: MONO, color: C.white }}>
                Explorar
              </span>
              <ArrowUpRight
                className="w-5 h-5"
                style={{
                  color: C.white,
                  transform: hovered ? "translate(3px, -3px)" : "translate(0, 0)",
                  transition: "transform 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── APP DATA ─── */
const APPS = [
  {
    id: "conecta", name: "CONECTA", tagline: "Cuando cada minuto cuenta",
    description: "Conecta voluntarios con protectoras durante emergencias. Alertas en tiempo real, geolocalización y coordinación.",
    icon: HeartHandshake, color: C.blue, href: "/conecta",
    features: [
      { icon: Bell, t: "Alertas en tiempo real" },
      { icon: MapPin, t: "Geolocalización" },
      { icon: Users, t: "Red de voluntarios" },
      { icon: Heart, t: "Sistema de ayudas" },
    ],
  },
  {
    id: "prepara", name: "PREPARA", tagline: "Prevenir es proteger",
    description: "Planes de emergencia personalizados, tarjeta de domicilio y mochila de vida. Todo para estar listo.",
    icon: ShieldCheck, color: C.orange, href: "/prepara",
    features: [
      { icon: Home, t: "Tarjeta de domicilio" },
      { icon: Backpack, t: "Mochila de vida" },
      { icon: ClipboardList, t: "Checklists" },
      { icon: FileText, t: "13 planes" },
    ],
  },
  {
    id: "aprende", name: "APRENDE", tagline: "El conocimiento salva vidas",
    description: "Cursos basados en FEMA. Quizzes interactivos, certificados y badges de logros.",
    icon: BookOpen, color: C.lilac, href: "/aprende",
    features: [
      { icon: GraduationCap, t: "Cursos certificados" },
      { icon: BookOpen, t: "Contenido FEMA" },
      { icon: Award, t: "Certificados PDF" },
      { icon: Users, t: "3 tracks formativos" },
    ],
  },
  {
    id: "fauna", name: "FAUNA", tagline: "Reporta y protege fauna silvestre",
    description: "Protocolos de actuación, centros de rescate cercanos y canal de reporte para fauna herida o en peligro.",
    icon: PawPrint, color: C.olive, href: "/fauna",
    features: [
      { icon: ShieldAlert, t: "Protocolos de actuación" },
      { icon: MapPin, t: "52 centros de fauna" },
      { icon: Bell, t: "Alertas a voluntarios" },
      { icon: TreePine, t: "España, Ecuador, Venezuela y Colombia" },
    ],
  },
  {
    id: "kids", name: "KIDS", tagline: "Pequeños héroes y heroínas",
    description: "Cuentos, juegos y actividades para que los más pequeños aprendan a proteger a los animales.",
    icon: Baby, color: C.pink, href: "/aprende/kids",
    features: [
      { icon: BookOpen, t: "Cuentos interactivos" },
      { icon: Award, t: "Juegos y quiz" },
      { icon: Heart, t: "Para 3-14 años" },
      { icon: Star, t: "Gana estrellas" },
    ],
  },
];

/* ─── PHASE DATA ─── */
const PHASES = [
  {
    phase: "ANTES", color: C.blue, title: "Prepararse\nsalva vidas",
    items: [
      { text: "Crea tu plan de emergencia personalizado", icon: ClipboardList },
      { text: "Prepara la mochila de vida para tus animales", icon: Backpack },
      { text: "Aprende protocolos con cursos certificados", icon: GraduationCap },
      { text: "Identifica centros de fauna y refugios cercanos", icon: MapPin },
    ],
  },
  {
    phase: "DURANTE", color: C.red, title: "Coordinar, actuar,\nconectar",
    items: [
      { text: "Activa alertas en tiempo real a voluntarios", icon: Bell },
      { text: "Coordina evacuaciones con protectoras", icon: Users },
      { text: "Reporta necesidades urgentes geolocalizadas", icon: MapPin },
      { text: "Accede a protocolos de emergencia para cada especie", icon: FileText },
    ],
  },
  {
    phase: "DESPUÉS", color: C.olive, title: "Recuperar, reconstruir,\naprender",
    items: [
      { text: "Reporta fauna herida o desorientada", icon: PawPrint },
      { text: "Conecta animales perdidos con familias", icon: Heart },
      { text: "Documenta daños para seguros y administración", icon: FileText },
      { text: "Comparte tu experiencia con la comunidad", icon: Users },
    ],
  },
];

/* ─── RESOURCE BLOCK (hover = lift + shadow) ─── */
function ResourceBlock({ r, i, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer"
      onClick={() => navigate(r.href)}
      style={{
        background: C.white, borderRadius: 4, border: `1px solid ${C.navy}10`, overflow: "hidden",
        transform: hovered ? "translateY(-5px)" : "translateY(0)",
        boxShadow: hovered ? "0 20px 50px rgba(45,53,97,0.15)" : "none",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      <div className="grid md:grid-cols-12 items-stretch">
        <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-between" style={{ background: r.bg }}>
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase block mb-3" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.7)" }}>
              {r.tag}
            </span>
            <span className="text-5xl font-bold" style={{ fontFamily: DISPLAY, color: C.white }}>
              0{i + 1}
            </span>
          </div>
          <ArrowRight className="w-5 h-5 mt-4" style={{
            color: "rgba(255,255,255,0.6)",
            transform: hovered ? "translateX(4px)" : "translateX(0)",
            transition: "transform 0.3s ease",
          }} />
        </div>
        <div className="md:col-span-9 p-8 md:p-10 flex flex-col justify-center">
          <h3 className="text-xl md:text-2xl lg:text-3xl tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.navy }}>
            {r.title}
          </h3>
          <p className="text-sm md:text-base leading-relaxed max-w-xl mb-5" style={{ color: "#666" }}>
            {r.desc}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold tracking-wider uppercase" style={{ fontFamily: MONO, color: r.color }}>
              Leer artículo
            </span>
            <ArrowRight className="w-4 h-4" style={{
              color: r.color,
              transform: hovered ? "translateX(4px)" : "translateX(0)",
              transition: "transform 0.3s ease",
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── RESOURCES ─── */
const RESOURCES = [
  {
    title: "Incendios forestales: cómo proteger a tus animales",
    tag: "PROTOCOLO", href: "/recursos/ciudadania/guias/proteger-animales-incendio-forestal",
    color: C.red, bg: C.red,
    desc: "Qué hacer antes, durante y después de un incendio. Protocolo paso a paso para fauna doméstica y silvestre.",
  },
  {
    title: "Mochila de vida: qué necesitas para evacuar con tus animales",
    tag: "GUÍA PRÁCTICA", href: "/recursos/ciudadania/guias/mochila-de-vida-emergencia-animales",
    color: C.orange, bg: C.orange,
    desc: "Checklist completo para preparar una mochila de emergencia para ti y tus animales. Con descargable PDF.",
  },
  {
    title: "Ola de calor: prevención y primeros auxilios",
    tag: "GUÍA ESTIVAL", href: "/recursos/ciudadania/guias/ola-de-calor-proteger-animales",
    color: C.olive, bg: C.olive,
    desc: "Golpe de calor en animales: signos, actuación inmediata y prevención. Adaptada a España y Ecuador.",
  },
  {
    title: "Pirotecnia y animales: cómo protegerlos de los fuegos artificiales",
    tag: "PREVENCIÓN", href: "/recursos/ciudadania/guias/pirotecnia-proteger-animales",
    color: "#a88efb", bg: "#a88efb",
    desc: "Refugio seguro, señales de pánico, protocolo por especie. Todo lo que necesitas antes de la noche de fuegos.",
  },
];

export default function HubLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: BODY, color: C.navy, background: C.white }}>
      <SEO
        title="Protección Animal en Emergencias"
        description="Plataforma solidaria y gratuita de tecnología cívica para proteger animales antes, durante y después de emergencias. Conecta voluntarios con protectoras, genera planes de evacuación personalizados. España, Ecuador, Venezuela y Colombia."
        path="/"
      />
      {/* Inject hover CSS */}
      <style>{HOVER_CSS}</style>

      {/* ═══════════════════ HEADER ═══════════════════ */}
      <header
        className="sticky top-0 z-50 px-6 md:px-12 py-4"
        style={{ background: C.white, borderBottom: `1px solid ${C.navy}15` }}
        data-testid="hub-header"
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3" style={{ background: "none", border: "none", cursor: "pointer" }}>
            <img src="/logos/logo-emergencia-animal.png" alt="Emergencia Animal" className="w-9 h-9 object-contain" />
            <span className="text-sm font-bold tracking-tight hidden sm:block" style={{ fontFamily: BODY, color: C.navy }}>
              Emergencia Animal
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {[
              { l: "Apps", h: "#apps" },
              { l: "Misión", h: "#about" },
              { l: "Recursos", h: "/recursos/ciudadania/guias", r: true },
              { l: "Blog", h: "/blog", r: true },
              { l: "Consultoría", h: "/consultoria", r: true },
            ].map((lnk) =>
              lnk.r ? (
                <button key={lnk.l} onClick={() => navigate(lnk.h)}
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{ fontFamily: BODY, color: C.navy, background: "none", border: "none", cursor: "pointer", opacity: 0.7 }}
                  data-testid={`hub-nav-${lnk.l.toLowerCase()}`}
                >{lnk.l}</button>
              ) : (
                <a key={lnk.l} href={lnk.h} className="px-4 py-2 text-sm font-medium" style={{ color: C.navy, opacity: 0.7, textDecoration: "none" }}>{lnk.l}</a>
              )
            )}
            <button onClick={() => navigate("/select-country")}
              className="px-4 py-2 text-sm font-medium flex items-center gap-1.5"
              style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
              data-testid="hub-nav-pais"
            ><Globe className="w-3.5 h-3.5" strokeWidth={2} /> País</button>
          </nav>

          <button onClick={() => navigate("/conecta")} data-testid="hub-header-access-btn"
            className="px-6 py-2.5 text-sm font-bold flex items-center gap-2"
            style={{ fontFamily: BODY, background: C.navy, color: C.white, border: "none", cursor: "pointer", borderRadius: 4 }}
          >Acceder <ArrowUpRight className="w-3.5 h-3.5" /></button>
        </div>
      </header>

      {/* ═══════════════════ 01 — MANIFEST / HERO ═══════════════════ */}
      <section className="relative overflow-hidden" style={{ background: C.navy }} data-testid="hub-hero">
        <img
          src="https://customer-assets.emergentagent.com/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/fm2c3kys_Dise%C3%B1o%20sin%20t%C3%ADtulo.webp"
          alt="Gato rescatado en brazos de un bombero durante una emergencia"
          className="absolute inset-0 w-full h-full object-cover object-center"
          data-testid="hub-hero-image"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(41,41,77,0.92) 0%, rgba(41,41,77,0.6) 45%, rgba(41,41,77,0.2) 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-36 pb-20 md:pb-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-10 lg:col-span-9">
              <Reveal>
                <p className="text-[11px] font-semibold tracking-[0.25em] uppercase mb-8" style={{ fontFamily: MONO, color: C.orange }}>
                  Plataforma solidaria &mdash; España, Ecuador, Venezuela y Colombia
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h1
                  className="text-[clamp(3rem,8vw,7rem)] font-normal leading-[0.92] tracking-tight mb-10"
                  style={{ fontFamily: DISPLAY, color: C.white }}
                >
                  Nadie<br />
                  se queda<br />
                  <span style={{ color: C.orange }}>atrás</span>
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-lg md:text-xl max-w-xl leading-relaxed mb-12" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Un ecosistema de tecnología diseñado para proteger a los animales
                  antes, durante y después de cualquier emergencia.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => navigate("/select-country")} data-testid="hub-hero-select-country-btn"
                    className="px-8 py-4 text-base font-bold flex items-center gap-2"
                    style={{ fontFamily: BODY, background: C.orange, color: C.navy, border: "none", cursor: "pointer", borderRadius: 4 }}
                  >Empezar <ArrowRight className="w-4 h-4" /></button>
                  <a href="#about"
                    className="px-8 py-4 text-base font-bold flex items-center gap-2"
                    style={{ fontFamily: BODY, background: "transparent", color: C.white, border: `1px solid rgba(255,255,255,0.3)`, textDecoration: "none", borderRadius: 4 }}
                    data-testid="hub-hero-about-btn"
                  >Conocer más</a>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Venezuela earthquake alert strip */}
          <Reveal delay={0.4}>
            <div
              className="mt-16 p-4 flex items-center gap-4 cursor-pointer transition-all"
              style={{ background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 4 }}
              onClick={() => navigate("/conecta")}
              data-testid="venezuela-alert-banner"
            >
              <ShieldAlert className="w-5 h-5 flex-shrink-0" style={{ color: C.red }} strokeWidth={2} />
              <div className="flex-1">
                <p className="font-bold text-sm" style={{ color: C.white }}>Terremoto de magnitud 7,5 con réplica</p>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>Países afectados: Colombia, Venezuela, Caribe Neerlandés, Curazao y Aruba — A 32 km de San Felipe, Yaracuy, Venezuela</p>
              </div>
              <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: C.red }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ 02 — EL PROBLEMA ═══════════════════ */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="hub-problem-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <EditorialLabel number="01" text="El problema" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight mb-8"
                style={{ fontFamily: DISPLAY, color: C.navy }}
              >
                Los animales siguen<br />
                <span style={{ color: C.red }}>fuera</span> de los planes<br />
                de emergencia
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base leading-relaxed max-w-xl mb-8" style={{ color: "#555" }}>
                En la DANA de Valencia de 2024, miles de animales quedaron atrapados
                porque no existían protocolos de evacuación animal. Los planes de
                emergencia municipales rara vez incluyen a los animales de compañía,
                y nunca a la fauna silvestre.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex flex-wrap gap-8 mb-6">
                {[
                  { v: "70%", d: "de planes de emergencia no incluyen animales" },
                  { v: "0", d: "protocolos nacionales para fauna silvestre" },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-3xl md:text-4xl font-bold" style={{ fontFamily: DISPLAY, color: C.red }}>{s.v}</p>
                    <p className="text-xs max-w-[180px] mt-1" style={{ color: "#888", fontFamily: MONO }}>{s.d}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-5">
            <Reveal delay={0.2}>
              <div style={{ borderRadius: 6, overflow: "hidden" }}>
                <img
                  src={IMG.problem}
                  alt="Un perro mirando a través de una ventana esperando ser rescatado"
                  className="w-full h-[350px] md:h-[450px] object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-[10px] mt-2 tracking-wider" style={{ fontFamily: MONO, color: "#aaa" }}>
                Cada animal es un individuo. Cada vida cuenta.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 03 — QUIENES ACTÚAN ═══════════════════ */}
      <section className="relative" data-testid="hub-people-section">
        <div className="w-full h-[400px] md:h-[500px] overflow-hidden relative">
          <img src={IMG.volunteers} alt="Voluntarios atendiendo a un perro rescatado" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(45,53,97,0.85) 0%, rgba(45,53,97,0.4) 60%, transparent 100%)" }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full">
              <Reveal>
                <EditorialLabel number="02" text="Quienes actúan" color={C.orange} />
                <h2
                  className="text-[clamp(1.8rem,4vw,3rem)] leading-[1.1] tracking-tight mb-4 max-w-lg"
                  style={{ fontFamily: DISPLAY, color: C.white }}
                >
                  Protectoras, voluntarios<br />
                  y ciudadanía <span style={{ color: C.orange }}>en primera línea</span>
                </h2>
                <p className="text-sm max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Cientos de personas y organizaciones trabajan sobre el terreno cada día para
                  proteger a los animales. Necesitan herramientas, coordinación y apoyo.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 04 — LA RESPUESTA DE EA ═══════════════════ */}
      <section id="about" className="px-6 md:px-12 py-20 md:py-28" style={{ background: C.offwhite }} data-testid="hub-about-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-8">
            <Reveal>
              <EditorialLabel number="03" text="La respuesta" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight mb-6"
                style={{ fontFamily: DISPLAY, color: C.navy }}
              >
                Emergencia Animal es<br />
                tecnología al servicio<br />
                <span style={{ color: C.orange }}>de quienes protegen</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base leading-relaxed max-w-2xl mb-6" style={{ color: "#555" }}>
                Nació de la experiencia directa. Del Huracán Katrina en 2005, cuando viajamos a EEUU
                para formarnos con protectoras. De años de estudio de protocolos. Y de la DANA de 2024,
                cuando todo lo aprendido se convirtió en herramientas reales.
              </p>
              <p className="text-base leading-relaxed max-w-2xl" style={{ color: "#555" }}>
                Un ecosistema de cinco aplicaciones que abarca todo el ciclo de la emergencia:
                desde la preparación hasta la recuperación, pasando por la coordinación
                en tiempo real y la formación ciudadana.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-4 flex flex-col gap-6">
            <Reveal delay={0.2}>
              <div className="p-6" style={{ background: C.navy, borderRadius: 6 }}>
                <p className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>Visión</p>
                <p className="text-lg leading-snug" style={{ fontFamily: DISPLAY, color: C.white }}>
                  Un mundo donde ningún animal sea olvidado en una emergencia.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="p-6" style={{ background: C.white, borderRadius: 6, border: `1px solid ${C.navy}15` }}>
                <p className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: MONO, color: C.olive }}>Alineación</p>
                <p className="text-sm leading-relaxed" style={{ color: "#555" }}>
                  Alineados con los objetivos de resiliencia de la ONU (MCR2030) y comprometidos con la
                  protección animal en España, Ecuador, Venezuela y Colombia.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 05 — ECOSISTEMA DE HERRAMIENTAS ═══════════════════ */}
      <section id="apps" className="py-20 md:py-28" data-testid="hub-apps-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <Reveal>
            <EditorialLabel number="04" text="El ecosistema" />
            <h2
              className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight"
              style={{ fontFamily: DISPLAY, color: C.navy }}
            >
              Cinco herramientas,<br />
              <span style={{ color: C.blue }}>una misión</span>
            </h2>
          </Reveal>
        </div>

        {/* Each app as a full-width band with hover */}
        {APPS.map((app, idx) => (
          <Reveal key={app.id} delay={idx * 0.05}>
            <AppBand app={app} idx={idx} navigate={navigate} />
          </Reveal>
        ))}
      </section>

      {/* ═══════════════════ 06 — ANTES / DURANTE / DESPUÉS ═══════════════════ */}
      <section className="py-20 md:py-28" data-testid="hub-phases-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <Reveal>
            <EditorialLabel number="05" text="Cómo funciona" />
            <h2
              className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight"
              style={{ fontFamily: DISPLAY, color: C.navy }}
            >
              Antes. Durante.<br />
              <span style={{ color: C.orange }}>Después.</span>
            </h2>
          </Reveal>
        </div>

        {PHASES.map((p, pi) => (
          <div key={p.phase} style={{ background: p.color }}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-3 h-3" style={{ background: C.white, borderRadius: 2 }} />
                  <span className="text-xs font-bold tracking-[0.2em]" style={{ fontFamily: MONO, color: C.white }}>{p.phase}</span>
                </div>
                <h3
                  className="text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tight mb-10 whitespace-pre-line"
                  style={{ fontFamily: DISPLAY, color: C.white }}
                >
                  {p.title}
                </h3>
              </Reveal>
              <div>
                {p.items.map((item, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <PhaseItem item={item} borderTop={i === 0 ? "none" : "1px solid rgba(255,255,255,0.15)"} />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ═══════════════════ 07 — TECNOLOGÍA Y DATOS (animated counters) ═══════════════════ */}
      <section className="py-20 md:py-28" style={{ background: C.navy }} data-testid="hub-stats-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 md:col-span-5">
              <Reveal>
                <EditorialLabel number="06" text="Impacto" color={C.orange} />
                <h2
                  className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-tight mb-6"
                  style={{ fontFamily: DISPLAY, color: C.white }}
                >
                  Datos reales,<br />
                  <span style={{ color: C.orange }}>acción real</span>
                </h2>
                <p className="text-sm leading-relaxed max-w-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                  Cada número representa vidas protegidas, personas coordinadas
                  y comunidades más preparadas.
                </p>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-7">
              <div className="grid grid-cols-2 gap-6">
                <CountUpNumber value="500+" label="Voluntarios activos" icon={Users} />
                <CountUpNumber value="50+" label="Organizaciones conectadas" icon={HeartHandshake} />
                <CountUpNumber value="1200+" label="Ayudas completadas" icon={Heart} />
                <CountUpNumber value="100+" label="Emergencias atendidas" icon={ShieldAlert} />
              </div>
            </div>
          </div>

          {/* Tech stack strip */}
          <Reveal delay={0.3}>
            <div className="mt-16 pt-12" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase mb-6" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.35)" }}>
                Tecnología
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: Zap, t: "Alertas en tiempo real", d: "Push + Telegram + WhatsApp" },
                  { icon: MapPin, t: "Geolocalización", d: "52 centros de fauna mapeados" },
                  { icon: Database, t: "Datos seguros", d: "Encriptación y GDPR compliant" },
                  { icon: BarChart3, t: "Plataforma abierta", d: "Sin coste para usuarios finales" },
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <t.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: C.orange }} strokeWidth={2} />
                    <div>
                      <p className="text-sm font-bold" style={{ color: C.white }}>{t.t}</p>
                      <p className="text-xs mt-0.5" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.4)" }}>{t.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ 08 — HISTORIA / CITA ═══════════════════ */}
      <section className="py-20 md:py-28" data-testid="hub-story-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-5 order-2 md:order-1">
            <Reveal>
              <div style={{ borderRadius: 6, overflow: "hidden" }}>
                <img src={IMG.cat} alt="Gato rescatado, primer plano" className="w-full h-[350px] md:h-[420px] object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 order-1 md:order-2">
            <Reveal>
              <EditorialLabel number="07" text="Historias" />
              <blockquote className="mb-6">
                <p
                  className="text-[clamp(1.5rem,3vw,2.2rem)] leading-[1.2] tracking-tight"
                  style={{ fontFamily: DISPLAY, color: C.navy }}
                >
                  &ldquo;Del Huracán Katrina a la DANA.
                  Llevamos veinte años preparándonos para que, cuando llegara
                  el momento, <span style={{ color: C.orange }}>ningún animal se quedara solo</span>.&rdquo;
                </p>
              </blockquote>
              <div className="flex items-center gap-3">
                <img src="/avatars/avatar-circle-nobg.png" alt="Núria Querol" className="w-10 h-10 object-cover" style={{ borderRadius: 4 }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: C.navy }}>Núria Querol</p>
                  <p className="text-xs" style={{ fontFamily: MONO, color: "#999" }}>Directora, Emergencia Animal</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 09 — EMERGENCIA VISUAL (FIRE) ═══════════════════ */}
      <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        <img src={IMG.fire} alt="Incendio forestal" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }}>
          <Reveal>
            <p
              className="text-center text-[clamp(1.5rem,4vw,3rem)] max-w-3xl px-6 leading-[1.15]"
              style={{ fontFamily: DISPLAY, color: C.white }}
            >
              Incendios. Inundaciones. DANAs.<br />
              <span style={{ color: C.orange }}>Los animales no pueden esperar.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ 10 — RECURSOS Y COMUNIDAD (redesigned) ═══════════════════ */}
      <section className="py-20 md:py-28" style={{ background: C.offwhite }} data-testid="hub-resources-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <Reveal>
            <EditorialLabel number="08" text="Recursos" />
            <h2
              className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight"
              style={{ fontFamily: DISPLAY, color: C.navy }}
            >
              Aprende, comparte<br />
              y <span style={{ color: C.olive }}>actúa en red</span>
            </h2>
          </Reveal>
        </div>

        {/* Big editorial resource blocks */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="space-y-6">
            {RESOURCES.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <ResourceBlock r={r} i={i} navigate={navigate} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-8 text-center">
              <button
                onClick={() => navigate("/recursos/ciudadania/guias")}
                className="px-8 py-3.5 text-sm font-bold flex items-center gap-2 mx-auto"
                style={{ fontFamily: BODY, background: C.navy, color: C.white, border: "none", cursor: "pointer", borderRadius: 4 }}
                data-testid="hub-guias-link"
              >
                Ver todas las guías <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ TEAM ═══════════════════ */}
      <section id="team" className="py-20 md:py-28" data-testid="hub-team-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <EditorialLabel number="09" text="El equipo" />
            <h2
              className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-tight mb-12"
              style={{ fontFamily: DISPLAY, color: C.navy }}
            >
              Quiénes <span style={{ color: C.blue }}>somos</span>
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 max-w-[900px]">
            {[
              {
                name: "Núria Querol", role: "Dirección",
                avatar: "/avatars/avatar-circle-nobg.png",
                bio: "Veterinaria, criminóloga y experta en gestión de emergencias con animales. Más de 20 años de experiencia en protección animal y desastres.",
                color: C.orange,
              },
              {
                name: "E1", role: "Desarrollo",
                avatar: "/avatars/avatar-e1-nobg.png",
                bio: "Agente de IA que diseña, programa y mantiene toda la plataforma. Inteligencia artificial al servicio de la vida.",
                color: C.blue,
              },
            ].map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1}>
                <div
                  className="ea-team-card p-8"
                  style={{ background: C.white, borderLeft: `4px solid ${m.color}`, borderRadius: 4, border: `1px solid ${C.navy}10`, borderLeftWidth: 4, borderLeftColor: m.color }}
                  data-testid={`hub-team-member-${i}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img src={m.avatar} alt={m.name} className="w-16 h-16 object-cover" style={{ borderRadius: 4 }} />
                    <div>
                      <p className="text-lg font-bold" style={{ fontFamily: DISPLAY, color: C.navy }}>{m.name}</p>
                      <p className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: m.color }}>{m.role}</p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "#666" }}>{m.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ TEAMING ═══════════════════ */}
      <section className="px-6 md:px-12 py-16 md:py-20" style={{ background: "#f8f7f4" }} data-testid="hub-teaming-section">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <div className="grid grid-cols-12 gap-0 items-stretch"
              style={{ background: "#fff", border: "1px solid rgba(45,53,97,0.06)", borderRadius: 4, overflow: "hidden" }}>
              <div className="col-span-12 md:col-span-8 p-8 md:p-12">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>
                  PROYECTO ALTRUISTA
                </p>
                <h2 className="text-2xl md:text-3xl tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.navy }}>
                  Emergencia Animal se mantiene con tu ayuda
                </h2>
                <p className="text-sm leading-relaxed mb-6" style={{ color: "#64748b" }}>
                  Somos un proyecto 100% altruista. No tenemos publicidad, ni patrocinadores, ni subvenciones.
                  <strong style={{ color: C.navy }}> Con solo 1€ al mes en Teaming</strong> ayudas a mantener la plataforma activa,
                  las guías actualizadas y la tecnología al servicio de los animales.
                </p>
                <a href="https://www.teaming.net/emergenciaanimal" target="_blank" rel="noopener noreferrer"
                  data-testid="hub-teaming-btn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold transition-all hover:-translate-y-0.5"
                  style={{
                    fontFamily: BODY,
                    background: C.orange,
                    color: C.navy,
                    border: "none",
                    borderRadius: 4,
                    textDecoration: "none",
                  }}>
                  <Heart size={16} className="fill-current" /> Colaborar con 1€/mes
                </a>
              </div>
              <div className="col-span-12 md:col-span-4 p-8 md:p-12 flex flex-col items-center justify-center text-center"
                style={{ background: "rgba(45,53,97,0.04)" }}>
                <Heart size={48} style={{ color: C.orange, marginBottom: 16 }} />
                <p className="text-4xl font-bold mb-1" style={{ fontFamily: DISPLAY, color: C.navy }}>1€</p>
                <p className="text-xs tracking-wider uppercase" style={{ fontFamily: MONO, color: "#94a3b8" }}>al mes</p>
                <p className="text-xs mt-3" style={{ color: "#94a3b8", lineHeight: 1.5 }}>
                  100% destinado al proyecto.<br />Sin compromiso. Cancela cuando quieras.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section className="py-24 md:py-36" style={{ background: C.navy }} data-testid="hub-cta-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8">
              <Reveal>
                <p className="text-[11px] font-semibold tracking-[0.25em] uppercase mb-8" style={{ fontFamily: MONO, color: C.orange }}>
                  Únete
                </p>
                <h2
                  className="text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tight mb-8"
                  style={{ fontFamily: DISPLAY, color: C.white }}
                >
                  ¿Listo para marcar<br />
                  <span style={{ color: C.orange }}>la diferencia?</span>
                </h2>
                <p className="text-base md:text-lg max-w-xl leading-relaxed mb-12" style={{ color: "rgba(255,255,255,0.6)" }}>
                  Únete a nuestra comunidad de personas que hacen posible que
                  ningún animal se quede atrás. Actúa, fórmate o colabora.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => navigate("/conecta")} data-testid="hub-cta-start-btn"
                    className="px-10 py-4 text-base font-bold flex items-center gap-2"
                    style={{ fontFamily: BODY, background: C.orange, color: C.navy, border: "none", cursor: "pointer", borderRadius: 4 }}
                  >Empezar ahora <ArrowRight className="w-5 h-5" /></button>
                  <button onClick={() => navigate("/prepara")}
                    className="px-8 py-4 text-base font-bold flex items-center gap-2"
                    style={{ fontFamily: BODY, background: "transparent", color: C.white, border: `1px solid rgba(255,255,255,0.3)`, cursor: "pointer", borderRadius: 4 }}
                  >Prepararse</button>
                  <button onClick={() => navigate("/aprende")}
                    className="px-8 py-4 text-base font-bold flex items-center gap-2"
                    style={{ fontFamily: BODY, background: "transparent", color: C.white, border: `1px solid rgba(255,255,255,0.3)`, cursor: "pointer", borderRadius: 4 }}
                  >Formarse</button>
                </div>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-4 hidden md:flex flex-col justify-end">
              <Reveal delay={0.2}>
                <div className="p-6" style={{ background: "rgba(255,255,255,0.06)", borderRadius: 4 }}>
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO, color: C.orange }}>
                    Teléfonos de emergencia
                  </p>
                  <div className="flex flex-col gap-3">
                    {[
                      { t: "112", d: "Emergencias generales (España)" },
                      { t: "062", d: "SEPRONA (Guardia Civil)" },
                      { t: "911", d: "Ecuador / Venezuela / Colombia" },
                      { t: "171", d: "Protección Civil (Venezuela)" },
                      { t: "123", d: "Emergencias (Colombia)" },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xl font-bold" style={{ fontFamily: DISPLAY, color: C.orange }}>{p.t}</span>
                        <span className="text-[10px] tracking-wider" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.4)" }}>{p.d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ TAGLINE ═══════════════════ */}
      <div className="px-6 py-4" style={{ background: C.orange }}>
        <p className="text-center text-xs font-semibold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color: C.navy }}>
          EMERGENCIA ANIMAL — NADIE SE QUEDA ATRÁS
        </p>
      </div>

    </div>
  );
}
